import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import Collapse from "@/components/ui/collapse";
import { Guardrails } from "@dreamhub/designsystem/icons";
import SimpleGuardrails from "@/pages/settings/components/dealsSettings/StageStudio/workflow/components/SimpleGuardrails";
import { useUpdateQuestionnaireMutation } from "@/pages/deals/hooks";
import { QuestionnaireBaseOutput } from "@/pages/deals/types";
import { DealScoreBadge } from "@/pages/deals/components/DealScoreBadge";
import { ScoreConfig, ScoreType } from "@/pages/deals/scoreConfig";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import ScoreGuardrails from "@/pages/settings/components/dealsSettings/StageStudio/workflow/components/ScoreGuardrails";

const TRANSLATION_PREFIX = "settings.deals.workflow.guardrails";

interface GuardrailsSectionProps {
  questionCount?: number;
  questionnaireId?: string;
  questionnaire?: QuestionnaireBaseOutput | null;
  onQuestionnaireUpdate?: () => Promise<any>;
  workflowType?: QuestionnaireType;
}

const GuardrailsSection: React.FC<GuardrailsSectionProps> = ({
  questionCount = 0,
  questionnaireId,
  questionnaire,
  onQuestionnaireUpdate,
  workflowType = QuestionnaireType.SIMPLE
}) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const isPOCQuestionnaire = workflowType === QuestionnaireType.POC;
  const isGeneralQuestionnaire = workflowType === QuestionnaireType.GENERAL;

  // Count required fields for general questionnaires
  const requiredFieldsCount = useMemo(() => {
    if (!isGeneralQuestionnaire || !questionnaire?.questions) return 0;
    return questionnaire.questions.filter((question) => question.isMandatory)
      .length;
  }, [isGeneralQuestionnaire, questionnaire?.questions]);

  const updateQuestionnaireMutation = useUpdateQuestionnaireMutation();

  useEffect(() => {
    if (questionnaire) {
      setIsEnabled(questionnaire.isGuardrails || false);
    }
  }, [questionnaire]);

  const handleSwitchChange = async (checked: boolean) => {
    if (!questionnaireId) {
      console.error("Cannot update guardrails: questionnaireId is missing");
      return;
    }

    setIsUpdating(true);

    try {
      await updateQuestionnaireMutation.mutateAsync({
        pathParams: { questionnaireId },
        data: { isGuardrails: checked }
      });
      setIsEnabled(checked);
      if (onQuestionnaireUpdate) {
        await onQuestionnaireUpdate();
      }
    } catch (error) {
      console.error("Failed to update questionnaire guardrails:", error);
      setIsEnabled(!checked);
    } finally {
      setIsUpdating(false);
    }
  };

  const getCollapseContent = () => {
    switch (workflowType) {
      case QuestionnaireType.SIMPLE:
        return <SimpleGuardrails questionCount={questionCount} />;
      case QuestionnaireType.SCORE:
        const totalFields = questionnaire?.questions?.length || 0;
        return (
          <ScoreGuardrails
            kpiValue={questionnaire?.kpiValue as ScoreType}
            totalFields={totalFields}
          />
        );
      default:
        return <SimpleGuardrails questionCount={questionCount} />;
    }
  };

  return (
    <div className="flex flex-col items-start gap-4 rounded-2xl border border-border-border1 bg-surface-widget-card p-4">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3 flex-1">
          <LogoIconContainer
            index={-1}
            icon={<Guardrails />}
            iconSize="md"
            variant={
              isGeneralQuestionnaire
                ? requiredFieldsCount > 0
                  ? "warning"
                  : "defaultGray"
                : "warning"
            }
          />
          <div className="flex flex-col gap-1">
            {isGeneralQuestionnaire ? (
              <Text variant="body">
                {requiredFieldsCount > 0
                  ? t(
                      `${TRANSLATION_PREFIX}.generalQuestionnaire.requiredFieldsApplied`
                    )
                  : t(
                      `${TRANSLATION_PREFIX}.generalQuestionnaire.noRequiredFields`
                    )}
              </Text>
            ) : (
              <div className="flex items-center gap-3">
                <Text variant="body">
                  {t(`${TRANSLATION_PREFIX}.workflowMustAchieveStart`, {
                    action: isPOCQuestionnaire
                      ? t(`${TRANSLATION_PREFIX}.have`)
                      : t(`${TRANSLATION_PREFIX}.achieve`)
                  })}
                </Text>
                <DealScoreBadge
                  scoreText={ScoreConfig.PASS}
                  scoreType={questionnaire?.kpiValue as ScoreType}
                  size="sm"
                />
                <Text variant="body">
                  {t(`${TRANSLATION_PREFIX}.workflowMustAchieveEnd`, {
                    statusOrScore: isPOCQuestionnaire
                      ? t(`${TRANSLATION_PREFIX}.status`)
                      : t(`${TRANSLATION_PREFIX}.score`)
                  })}
                </Text>
              </div>
            )}
            {isPOCQuestionnaire && (
              <Text variant="bodySecondary">
                {t(`${TRANSLATION_PREFIX}.pocSubtitle`)}
              </Text>
            )}
          </div>
        </div>
        {!isGeneralQuestionnaire && (
          <Switch
            checked={isEnabled}
            onCheckedChange={handleSwitchChange}
            disabled={isUpdating}
          />
        )}
      </div>

      {!isPOCQuestionnaire && !isGeneralQuestionnaire && (
        <>
          <div className="w-full border border-border-border1" />
          <Collapse
            triggerText={t(
              `${TRANSLATION_PREFIX}.collapseTrigger.${workflowType}`
            )}
          >
            {getCollapseContent()}
          </Collapse>
        </>
      )}
    </div>
  );
};

export default GuardrailsSection;
